import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { X, Send, RefreshCw, Loader2, MessageSquare, Video, FileText, AlertTriangle, User, Sparkles, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { avatarVideoService, type AgentMessage, type VideoAgentSession, type DraftResult } from '@/services/avatar-video.service';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';

// Format a unix timestamp (seconds or ms). Same-day messages show only the
// time; older messages show "DD MMM, HH:MM" so the day is unambiguous.
const formatMessageTime = (created_at?: number): string => {
  if (!created_at) return '';
  // HeyGen sometimes returns seconds; normalize to ms if value looks like seconds
  const ms = created_at < 10_000_000_000 ? created_at * 1000 : created_at;
  try {
    const d = new Date(ms);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const same_day = d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
    if (same_day) return time;
    const date = d.toLocaleDateString([], { day: '2-digit', month: 'short' });
    return `${date}, ${time}`;
  } catch {
    return '';
  }
};

const CustomVideoPlayer = lazy(() => import('./CustomVideoPlayer'));

interface EditAvatarVideoModalProps {
  contentItem: any;
  courseId: string;
  onClose: () => void;
  onVideoUpdated?: (new_url: string) => void;
}

export default function EditAvatarVideoModal({
  contentItem,
  courseId,
  onClose,
  onVideoUpdated,
}: EditAvatarVideoModalProps) {
  const [session, setSession] = useState<VideoAgentSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [revisionMessage, setRevisionMessage] = useState('');
  const [isRevising, setIsRevising] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRegenPanel, setShowRegenPanel] = useState(false);
  const [editedNarration, setEditedNarration] = useState('');
  const [videoUrl, setVideoUrl] = useState(contentItem.media_assets?.s3_video_url || '');
  // Async render progress label (set while polling after revise/regenerate)
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  // Draft awaiting human approval (chat-mode review).
  const [draft, setDraft] = useState<DraftResult | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Friendly label for an avatar_render lifecycle status.
  const renderStatusLabel = (status?: string): string => {
    switch (status) {
      case 'queued': return 'Queued — waiting for an available render slot…';
      case 'dispatched':
      case 'agent_processing': return 'Generating draft…';
      case 'awaiting_approval': return 'Draft ready — review & approve';
      case 'agent_approved': return 'Approved — rendering…';
      case 'video_rendering': return 'Rendering video…';
      case 'ready_to_finalize': return 'Finalizing…';
      default: return 'Processing…';
    }
  };

  // Drive the draft→render lifecycle: poll until the draft pauses for review, fails,
  // or the final video completes. Updates progress + draft state along the way.
  const runDraftFlow = async (toastId: string) => {
    const { phase, draft: d } = await avatarVideoService.pollUntilDraftOrDone(contentItem._id, {
      onTick: (dd) => {
        setProgress(typeof dd.progress === 'number' ? dd.progress : null);
        setProcessingStatus(renderStatusLabel(dd.avatar_render_status));
        // Keep the chat live with the agent's storyboard/conversation as it evolves.
        if (dd.messages && dd.messages.length) {
          setSession(prev => prev
            ? { ...prev, messages: dd.messages, session_id: dd.session_id || prev.session_id }
            : prev);
        }
      },
    });
    if (phase === 'awaiting_approval') {
      setDraft(d);
      setProcessingStatus(null);
      setProgress(null);
      toast('Draft ready — review and approve', { id: toastId, icon: '📝' });
    } else if (phase === 'completed') {
      setDraft(null);
      if (d.s3_video_url) {
        setVideoUrl(d.s3_video_url);
        onVideoUpdated?.(d.s3_video_url);
      }
      setProcessingStatus(null);
      setProgress(null);
      toast.success('Video ready!', { id: toastId });
      await loadSession();
    } else {
      setProcessingStatus(null);
      setProgress(null);
      toast.error('Generation failed', { id: toastId });
    }
  };

  const handleApproveDraft = async () => {
    setIsApproving(true);
    toast.loading('Approving draft…', { id: 'approve' });
    try {
      const result = await avatarVideoService.approveDraft(contentItem._id);
      if (!result.success) {
        toast.error(result.error || 'Approve failed', { id: 'approve' });
        return;
      }
      setDraft(null);
      setProcessingStatus(renderStatusLabel('agent_approved'));
      toast.loading('Rendering final video…', { id: 'approve' });
      await runDraftFlow('approve');
    } catch (error: any) {
      if (error?.code === 'POLL_TIMEOUT') toast(error.message, { id: 'approve', icon: '⏳' });
      else toast.error(error?.message || 'Approve failed', { id: 'approve' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!revisionMessage.trim() || revisionMessage.trim().length < 3) {
      toast.error('Describe the change you want');
      return;
    }
    setIsRevising(true);
    toast.loading('Requesting changes…', { id: 'revise' });
    try {
      const result = await avatarVideoService.requestChanges(contentItem._id, revisionMessage.trim());
      if (!result.success) {
        toast.error(result.error || 'Request failed', { id: 'revise' });
        return;
      }
      setRevisionMessage('');
      setDraft(null);
      toast.loading('Re-drafting…', { id: 'revise' });
      await runDraftFlow('revise');
    } catch (error: any) {
      if (error?.code === 'POLL_TIMEOUT') toast(error.message, { id: 'revise', icon: '⏳' });
      else toast.error(error?.message || 'Request failed', { id: 'revise' });
    } finally {
      setIsRevising(false);
    }
  };

  // Load session on mount, and RESUME the right UI if a render is already in flight
  // (so closing/reopening the modal mid-review or mid-render restores state).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadSession();
      if (cancelled) return;

      // Fetch the LIVE render status — the contentItem prop is often stale (it carries
      // whatever the content tree loaded, usually the previous 'completed' state), so we
      // must not rely on it to decide whether a render is in flight.
      const live = await avatarVideoService.getVideoStatus(contentItem._id);
      if (cancelled) return;
      const status: string | undefined = live.avatar_render_status;
      // Keep the freshest finished video on screen.
      if (status === 'completed' && live.s3_video_url) setVideoUrl(live.s3_video_url);

      const IN_FLIGHT = ['queued', 'dispatched', 'agent_processing', 'awaiting_approval', 'agent_approved', 'video_rendering', 'ready_to_finalize'];
      if (!status || !IN_FLIGHT.includes(status)) return;

      // Reflect the in-flight state immediately (before the first poll tick) so reopening
      // the modal instantly shows "generating/rendering" instead of the old video.
      setProcessingStatus(renderStatusLabel(status));

      // A draft is paused for review → restore the review UI + show its storyboard in chat.
      if (status === 'awaiting_approval') {
        const d = await avatarVideoService.getDraft(contentItem._id);
        if (cancelled) return;
        if (d.avatar_render_status === 'awaiting_approval') {
          if (d.messages && d.messages.length) {
            setSession(prev => prev ? { ...prev, messages: d.messages, session_id: d.session_id || prev.session_id } : prev);
          }
          setProcessingStatus(null);
          setDraft(d);
          return;
        }
      }

      // Otherwise a render is still progressing → resume the live loader/poll.
      toast.loading('Resuming…', { id: 'resume' });
      try {
        await runDraftFlow('resume');
      } catch (error: any) {
        if (error?.code === 'POLL_TIMEOUT') toast(error.message, { id: 'resume', icon: '⏳' });
        else toast.error(error?.message || 'Failed to resume', { id: 'resume' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentItem._id]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages]);

  const loadSession = async () => {
    setIsLoadingSession(true);
    const result = await avatarVideoService.getSession(contentItem._id);
    setSession(result);
    setEditedNarration(result.narration_script || '');
    setIsLoadingSession(false);
  };

  const handleRevise = async () => {
    if (!revisionMessage.trim() || revisionMessage.trim().length < 5) {
      toast.error('Revision message must be at least 5 characters');
      return;
    }

    setIsRevising(true);
    toast.loading('Sending revision to HeyGen Video Agent...', { id: 'revise' });

    try {
      const result = await avatarVideoService.revise(contentItem._id, revisionMessage.trim());

      if (!result.success) {
        toast.error(result.error || 'Revision failed', { id: 'revise' });
        return;
      }

      if (result.dispatched) {
        // Async chat-mode: a draft is generated for review before the final render.
        setRevisionMessage('');
        toast.loading('Generating draft…', { id: 'revise' });
        await runDraftFlow('revise');
      } else if (result.s3_video_url) {
        // Sync fallback (HEYGEN_ASYNC_RENDER=false)
        setVideoUrl(result.s3_video_url);
        setRevisionMessage('');
        onVideoUpdated?.(result.s3_video_url);
        toast.success('Video revised successfully!', { id: 'revise' });
        await loadSession();
      } else {
        toast.error('Revision failed', { id: 'revise' });
      }
    } catch (error: any) {
      if (error?.code === 'POLL_TIMEOUT') {
        toast(error.message, { id: 'revise', icon: '⏳' });
      } else {
        toast.error(error?.message || 'Revision failed', { id: 'revise' });
      }
    } finally {
      setProcessingStatus(null);
      setIsRevising(false);
    }
  };

  const handleRegenerate = async () => {
    if (!editedNarration.trim() || editedNarration.trim().length < 20) {
      toast.error('Narration must be at least 20 characters');
      return;
    }

    setIsRegenerating(true);
    toast.loading('Regenerating full video with HeyGen Video Agent...', { id: 'regen' });

    try {
      const result = await avatarVideoService.regenerate(contentItem._id, editedNarration.trim());

      if (!result.success) {
        toast.error(result.error || 'Regeneration failed', { id: 'regen' });
        return;
      }

      if (result.dispatched) {
        // Async chat-mode: a draft is generated for review before the final render.
        setShowRegenPanel(false);
        toast.loading('Generating draft…', { id: 'regen' });
        await runDraftFlow('regen');
      } else if (result.s3_video_url) {
        // Sync fallback (HEYGEN_ASYNC_RENDER=false)
        setVideoUrl(result.s3_video_url);
        setShowRegenPanel(false);
        onVideoUpdated?.(result.s3_video_url);
        toast.success('Video regenerated successfully!', { id: 'regen' });
        await loadSession();
      } else {
        toast.error('Regeneration failed', { id: 'regen' });
      }
    } catch (error: any) {
      if (error?.code === 'POLL_TIMEOUT') {
        toast(error.message, { id: 'regen', icon: '⏳' });
      } else {
        toast.error(error?.message || 'Regeneration failed', { id: 'regen' });
      }
    } finally {
      setProcessingStatus(null);
      setIsRegenerating(false);
    }
  };

  // A revision re-renders the FULL video via the Video Agent (not a cheap partial edit),
  // so its cost ≈ a full render — estimate from the current narration length.
  const estimateRevisionCost = () => {
    const words = (session?.narration_script || '').trim().split(/\s+/).filter(Boolean).length;
    if (!words) return '~$2';
    const seconds = Math.ceil(words / 2.5);
    return `~$${(seconds * 0.0333).toFixed(2)}`;
  };
  const estimateRegenCost = () => {
    const words = editedNarration.trim().split(/\s+/).filter(Boolean).length;
    const seconds = Math.ceil(words / 2.5);
    const cost = (seconds * 0.0333).toFixed(2);
    return `~$${cost}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-5xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-brand-purple-50 to-white">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-brand-purple-600" />
            <h2 className="text-sm font-semibold text-gray-900">Edit Avatar Video</h2>
            <span className="text-xs text-gray-400 truncate max-w-[240px]">{contentItem.title}</span>
            {/* Live render-state pill — always identifies what's happening */}
            {draft ? (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-purple-100 text-brand-purple-700">
                <Sparkles className="w-3 h-3" /> Draft ready — review
              </span>
            ) : processingStatus ? (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                <Loader2 className="w-3 h-3 animate-spin" />
                {processingStatus}{typeof progress === 'number' && progress > 0 ? ` ${progress}%` : ''}
              </span>
            ) : null}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body — Split Panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel — Video + Narration */}
          <div className="w-1/2 border-r flex flex-col overflow-y-auto">
            {/* Video Player */}
            <div className="p-4">
              {draft ? (
                /* Draft ready — review & approve */
                <div className="rounded-lg border border-brand-purple-200 bg-brand-purple-50/40 p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-4 h-4 text-brand-purple-600" />
                    <p className="text-sm font-semibold text-brand-purple-800">Draft ready — review it</p>
                  </div>

                  {/* Preview resources (storyboard images / draft video) */}
                  {draft.resources && draft.resources.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 mb-3 max-h-56 overflow-y-auto">
                      {draft.resources.map((r) => {
                        const media = r.preview_url || r.url || r.thumbnail_url;
                        if (!media) return null;
                        const isVideo = (r.resource_type || '').toLowerCase().includes('video') || /\.mp4($|\?)/i.test(media);
                        return isVideo ? (
                          <video key={r.resource_id} src={media} controls className="w-full rounded border border-brand-purple-100 bg-black" />
                        ) : (
                          <img key={r.resource_id} src={media} alt="draft preview" className="w-full rounded border border-brand-purple-100 object-cover" />
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500 mb-3">
                      No preview media on this draft — review the storyboard in the chat on the right.
                    </p>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleApproveDraft}
                      disabled={isApproving}
                      className="flex-1 px-3 py-2 bg-brand-purple-600 text-white rounded-lg text-xs font-medium hover:bg-brand-purple-700 disabled:bg-gray-300 transition-colors flex items-center justify-center gap-1.5"
                    >
                      {isApproving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                      Approve &amp; Render
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    Want changes? Describe them in the chat on the right and click <span className="font-medium">Request changes</span>.
                  </p>
                </div>
              ) : processingStatus ? (
                <div className="aspect-video bg-brand-purple-50 border border-brand-purple-200 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-center px-4 w-full">
                    <Loader2 className="w-8 h-8 text-brand-purple-500 animate-spin mx-auto mb-2" />
                    <p className="text-sm font-medium text-brand-purple-700">{processingStatus}</p>
                    {typeof progress === 'number' && progress > 0 && (
                      <div className="mt-2 mx-auto w-2/3 h-1.5 bg-brand-purple-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-purple-500 rounded-full transition-all" style={{ width: `${Math.min(100, progress)}%` }} />
                      </div>
                    )}
                    <p className="text-[11px] text-brand-purple-500 mt-1">
                      You can safely close this — it keeps running in the background.
                    </p>
                  </div>
                </div>
              ) : videoUrl ? (
                <Suspense fallback={
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-brand-purple-400 animate-spin" />
                  </div>
                }>
                  <div className="rounded-lg overflow-hidden">
                    <CustomVideoPlayer src={videoUrl} title={contentItem.title} />
                  </div>
                </Suspense>
              ) : (
                <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Video className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">No video available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Narration Script */}
            <div className="px-4 pb-4 flex-1">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  Narration Script
                </label>
                {session?.narration_script && (
                  <span className="text-[10px] text-gray-400">
                    {session.narration_script.split(/\s+/).length} words
                  </span>
                )}
              </div>
              {isLoadingSession ? (
                <SkeletonLoader type="custom" className="h-32 w-full rounded-lg" />
              ) : (
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap">
                  {session?.narration_script || 'No narration script saved for this content item.'}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel — Revision Chat */}
          <div className="w-1/2 flex flex-col">
            {/* Tab: Revise / Full Regen */}
            <div className="flex border-b">
              <button
                onClick={() => setShowRegenPanel(false)}
                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                  !showRegenPanel
                    ? 'text-brand-purple-700 border-b-2 border-brand-purple-600 bg-brand-purple-50/50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5 inline mr-1" />
                Revise ({estimateRevisionCost()})
              </button>
              <button
                onClick={() => setShowRegenPanel(true)}
                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                  showRegenPanel
                    ? 'text-amber-700 border-b-2 border-amber-600 bg-amber-50/50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <RefreshCw className="w-3.5 h-3.5 inline mr-1" />
                Full Regeneration ({estimateRegenCost()})
              </button>
            </div>

            {!showRegenPanel ? (
              /* Scene Revision Tab */
              <>
                {/* Conversation History */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {isLoadingSession ? (
                    <div className="space-y-3">
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className="max-w-[85%] w-2/3 space-y-1.5">
                            <SkeletonLoader type="custom" className="h-3 w-20 rounded" />
                            <SkeletonLoader type="custom" className="h-12 w-full rounded-lg" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : session?.messages && session.messages.length > 0 ? (
                    session.messages.map((msg, i) => {
                      const time_label = formatMessageTime(msg.created_at);
                      return (
                        <div
                          key={i}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${
                            msg.role === 'user'
                              ? 'bg-brand-purple-100 text-brand-purple-900'
                              : msg.type === 'error'
                                ? 'bg-red-50 text-red-700 border border-red-200'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-1">
                                {msg.role === 'model' ? (
                                  <Sparkles className="w-3 h-3 text-brand-purple-500" />
                                ) : (
                                  <User className="w-3 h-3 text-brand-purple-600" />
                                )}
                                <span className="text-[10px] font-semibold text-gray-500">
                                  {msg.role === 'model' ? 'HeyGen Agent' : 'You'}
                                </span>
                              </div>
                              {time_label && (
                                <span className="text-[10px] text-gray-400 flex-shrink-0">{time_label}</span>
                              )}
                            </div>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center py-8">
                        <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-xs text-gray-400">
                          {session?.session_id
                            ? 'No conversation history available'
                            : 'No Video Agent session found. Use Full Regeneration to create one.'}
                        </p>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Revision Input */}
                <div className="border-t p-3">
                  {!session?.session_id && !draft ? (
                    <p className="text-xs text-gray-400 text-center py-2">
                      No active session. Use the Full Regeneration tab to create a new video.
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <textarea
                        value={revisionMessage}
                        onChange={e => setRevisionMessage(e.target.value)}
                        placeholder="Describe what to change... (e.g., 'Make the intro shorter', 'Add more detail about passwords')"
                        className="flex-1 px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white resize-none focus:ring-1 focus:ring-brand-purple-300 focus:border-brand-purple-300"
                        rows={2}
                        disabled={isRevising}
                      />
                      <button
                        onClick={draft ? handleRequestChanges : handleRevise}
                        disabled={isRevising || !revisionMessage.trim()}
                        className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg text-xs font-medium hover:bg-brand-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1 self-end whitespace-nowrap"
                      >
                        {isRevising ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        {draft ? 'Request changes' : 'Revise'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Full Regeneration Tab */
              <div className="flex-1 flex flex-col p-4">
                <div className="flex items-center gap-2 mb-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <p className="text-[10px] text-amber-700">
                    Full regeneration creates a NEW Video Agent session from your edited narration ({estimateRegenCost()}).
                    Note: both Revise and Regenerate re-render the entire video, so they cost about the same — use Revise for small tweaks, Regenerate when the narration needs major changes.
                  </p>
                </div>

                <label className="text-xs font-semibold text-gray-700 mb-2">Edit Narration Script</label>
                <textarea
                  value={editedNarration}
                  onChange={e => setEditedNarration(e.target.value)}
                  className="flex-1 px-3 py-2.5 text-xs rounded-lg border border-gray-200 bg-white resize-none focus:ring-1 focus:ring-amber-300 focus:border-amber-300 leading-relaxed"
                  placeholder="Enter the narration script for the video..."
                  disabled={isRegenerating}
                />

                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-gray-400">
                    {editedNarration.trim().split(/\s+/).filter(Boolean).length} words |
                    Est. ~{Math.ceil(editedNarration.trim().split(/\s+/).filter(Boolean).length / 2.5)}s
                  </span>
                  <button
                    onClick={handleRegenerate}
                    disabled={isRegenerating || editedNarration.trim().length < 20}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    {isRegenerating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Regenerate ({estimateRegenCost()})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
